import { ResultsContainer } from '@components/organisms/ResultsContainer';
import { ResultsType } from '@types';
import { FC, useEffect, useRef, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetSpaces, GetSpacesInput } from '@services/useGetSpaces';
import { useGetPropertyTypes } from '@services/useGetPropertyTypes';
import { useData } from '@hooks/useData';
import { SpaceCardProps } from '@components/molecules/SpaceCard';
import { AppContainer } from '@components/organisms/AppContainer';
import { SpacesFilter, SpacesFilterValues } from '@components/molecules/SpacesFilter';
import { useTranslation } from 'react-i18next';
import { CtaBanner } from '@components/organisms/CtaBanner';
import { useGetBannerCustomAlert } from '@services/useGetBannerCustomAlert';
import { useCustomNavigate } from '@hooks/useCustomNavigate';

export const SpacesList: FC = () => {
    const { request } = useGetSpaces();
    const { request: fetchPropertyTypes, propertyTypes } = useGetPropertyTypes();
    const { spaces: spacesData, isLoadingSpaces } = useData();
    const { t } = useTranslation();
    const { navigate } = useCustomNavigate();
    const [searchParams] = useSearchParams();
    const { banner, request: fetchBanner, loading: bannerLoading } = useGetBannerCustomAlert();

    // Ler parâmetros da URL para inicializar filtros
    const initialFilters: SpacesFilterValues = {};
    const featuredType = searchParams.get('featuredType');
    const spaceTypeCategory = searchParams.get('spaceTypeCategory');
    const spaceTypeUuid = searchParams.get('spaceTypeUuid');

    if (featuredType) {
        initialFilters.featuredType = featuredType;
    }
    if (spaceTypeCategory) {
        initialFilters.businessCategory = spaceTypeCategory;
    }
    if (spaceTypeUuid) {
        initialFilters.propertyTypes = [spaceTypeUuid];
    }

    const [filters, setFilters] = useState<SpacesFilterValues>(initialFilters);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [orderBy, setOrderBy] = useState<string>('price');
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [selectedOrder, setSelectedOrder] = useState<string>('price_asc');
    const [isMapVisible, setIsMapVisible] = useState<boolean>(true);
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const perPage = 6;

    const checkIsMobile = useCallback(() => {
        setIsMobile(window.innerWidth < 768);
    }, []);

    useEffect(() => {
        checkIsMobile();
        window.addEventListener('resize', checkIsMobile);
        return () => window.removeEventListener('resize', checkIsMobile);
    }, [checkIsMobile]);

    const handleToggleMap = (visible: boolean) => {
        setIsMapVisible(visible);
    };

    useEffect(() => {
        fetchBanner();
        fetchPropertyTypes();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        // Fetch spaces on component mount with initial filters
        const apiFilters: GetSpacesInput = {};

        if (initialFilters.featuredType) {
            apiFilters.featuredType = initialFilters.featuredType;
        }
        if (initialFilters.businessCategory) {
            apiFilters.spaceTypeCategory = initialFilters.businessCategory;
        }
        if (initialFilters.propertyTypes && initialFilters.propertyTypes.length > 0) {
            apiFilters.spaceTypeUuids = initialFilters.propertyTypes;
        }

        apiFilters.page = 1;
        apiFilters.perPage = perPage;
        apiFilters.orderBy = 'price';
        apiFilters.sortDesc = false;

        request(apiFilters);
        // eslint-disable-next-line
    }, []);

    // Fetch spaces when filters, page, or order change
    useEffect(() => {
        const apiFilters: GetSpacesInput = {};

        if (filters.searchQuery) {
            apiFilters.search = filters.searchQuery;
        }
        if (filters.propertyCondition) {
            apiFilters.spaceStatusUuid = filters.propertyCondition;
        }
        if (filters.businessCategory) {
            apiFilters.spaceTypeCategory = filters.businessCategory;
        }
        if (filters.priceMin !== undefined) {
            apiFilters.priceMin = filters.priceMin;
        }
        if (filters.priceMax !== undefined) {
            apiFilters.priceMax = filters.priceMax;
        }
        if (filters.areaMin !== undefined) {
            apiFilters.areaMin = filters.areaMin;
        }
        if (filters.areaMax !== undefined) {
            apiFilters.areaMax = filters.areaMax;
        }
        if (filters.propertyTypes && filters.propertyTypes.length > 0) {
            apiFilters.spaceTypeUuids = filters.propertyTypes;
        }
        if (filters.featuredType) {
            apiFilters.featuredType = filters.featuredType;
        }

        // Add pagination
        apiFilters.page = currentPage;
        apiFilters.perPage = perPage;

        // Add ordering (always send)
        apiFilters.orderBy = orderBy;
        apiFilters.sortDesc = sortDesc;

        request(apiFilters);
        // eslint-disable-next-line
    }, [filters, currentPage, orderBy, sortDesc]);

    // Transform API data to SpaceCard format
    const spaces: SpaceCardProps[] = useMemo(() => {
        if (!spacesData?.data) return [];

        const energyClasses = ['A', 'B', 'C', 'D', 'E', 'F'];

        return spacesData.data.map((space, idx) => {
            const firstGalleryImage = (space.galleries?.[0] as { url?: string } | undefined)?.url;

            return {
                address: space.address,
                uuid: space.uuid,
                merchant: space.spaceType.name,
                name: space.featuredType.label,
                image: firstGalleryImage || `https://picsum.photos/200/300?random=${idx}`,
                price: parseFloat(space.price.replace(/[^\d,]/g, '').replace(',', '.')),
                initialFavorite: space.like,
                truncateTitle: true,
                energyClassification: energyClasses[Math.floor(Math.random() * energyClasses.length)],
                areaM2: Math.round(parseFloat(space.areaM2)).toString(),
                latitude: space.latitude,
                longitude: space.longitude,
            };
        });
    }, [spacesData]);

    // Map pins for mobile
    const mapPins: [number, number][] = useMemo(() => {
        return spaces
            .filter(space => space.latitude && space.longitude)
            .map(space => {
                const lat = parseFloat(space.latitude);
                const lng = parseFloat(space.longitude);
                return [lat, lng] as [number, number];
            })
            .filter(([lat, lng]) => !isNaN(lat) && !isNaN(lng));
    }, [spaces]);

    const businessCategoryOptions = useMemo(() => {
        if (!propertyTypes || propertyTypes.length === 0) return [];
        return propertyTypes.map((category) => ({ value: category.key, text: category.value }));
    }, [propertyTypes]);


    const handleApplyFilters = (newFilters: SpacesFilterValues) => {
        setFilters(newFilters);
        setCurrentPage(1); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({});
        setCurrentPage(1);
    };

    const handleCreateAlert = () => {
        console.log('Create alert with filters:', filters);
        // TODO: Implement alert creation logic
    };

    const handleOrderChange = (value: string) => {
        setSelectedOrder(value);

        if (!value) {
            setOrderBy('price');
            setSortDesc(false);
            return;
        }

        // Map select value to API parameters
        const orderMap: Record<string, { field: string; desc: boolean }> = {
            'price_asc': { field: 'price', desc: false },
            'price_desc': { field: 'price', desc: true },
            'area_asc': { field: 'area_m2', desc: false },
            'area_desc': { field: 'area_m2', desc: true },
            'created_at_desc': { field: 'created_at', desc: true },
            'created_at_asc': { field: 'created_at', desc: false },
        };

        const order = orderMap[value];
        if (order) {
            setOrderBy(order.field);
            setSortDesc(order.desc);
        }
    };

    const resultsContainerRef = useRef<HTMLDivElement>(null);
    const isInitialMount = useRef(true);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Scroll to top when page changes (but not on initial mount)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        const { current: element } = resultsContainerRef;
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [currentPage]);

    // Calculate total pages
    const totalPages = Math.ceil((spacesData?.recordsTotal || 0) / perPage);

    const handleBannerClick = () => {
        if (banner?.content_banner_custom_alert_link) {
            navigate(banner.content_banner_custom_alert_link);
        }
    };

    const ctaBannerElement = !bannerLoading && banner ? (
        <CtaBanner
            variant="background"
            subtitle={banner.content_banner_custom_alert_subtitle}
            title={banner.content_banner_custom_alert_title || ''}
            description={banner.content_banner_custom_alert_description}
            buttonText={banner.content_banner_custom_alert_button_text}
            onButtonClick={handleBannerClick}
            imageUrl={banner.content_banner_custom_alert_image}
            imageAlt={banner.content_banner_custom_alert_title || 'Banner'}
        />
    ) : undefined;

    return (
        <AppContainer ctaBanner={ctaBannerElement}>
            <ResultsContainer
                ref={resultsContainerRef}
                searchQuery=""
                loading={isLoadingSpaces}
                total={spacesData?.recordsTotal || 0}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                order={{
                    options: [
                        {
                            value: 'price_asc',
                            text: t('order-options.price-asc'),
                        },
                        {
                            value: 'price_desc',
                            text: t('order-options.price-desc'),
                        },
                        {
                            value: 'area_desc',
                            text: t('order-options.area-desc'),
                        },
                        {
                            value: 'area_asc',
                            text: t('order-options.area-asc'),
                        },
                    ],
                    onChange: handleOrderChange,
                    value: selectedOrder,
                }}
                results={spaces}
                type={ResultsType.spaces}
                isMapVisible={isMapVisible}
                filters={
                    <SpacesFilter
                        onApplyFilters={handleApplyFilters}
                        onClearFilters={handleClearFilters}
                        onCreateAlert={handleCreateAlert}
                        initialValues={filters}
                        businessCategoryOptions={businessCategoryOptions}
                        showMapToggle={isMobile}
                        isMapVisible={isMapVisible}
                        onToggleMap={handleToggleMap}
                        mapPins={mapPins}
                    />
                }
            />
        </AppContainer>
    );
};
